import { authedFetch } from "@/lib/authed-fetch";
import { useState } from "react";
import { User, LogOut, Settings, CalendarOff, Scale } from "lucide-react";
import LegalModal, { type LegalKind } from "../share/ui/LegalModal";
import { useRegisterUserMenu } from "@/lib/user-menu-presence";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/auth-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { type UserStatusKey } from "@shared/status-config";
import { StatusBubble, StatusPicker } from "./status-bubble";
import { AccountSettingsDialog } from "./account-settings-dialog";
import { TimeOffDialog } from "./time-off/TimeOffDialog";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface StatusData {
  id: number;
  username: string;
  availabilityStatus: string;
  statusTimerMinutes?: number | null;
  statusExpiresAt?: string | null;
  statusTimers?: Record<string, number>;
}

export function UserMenu() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  // Impressum / Datenschutz overlay (task #140): the fixed legal footer is
  // hidden while logged in (the chat dock covered it), so the links live
  // at the very bottom of this menu instead.
  const [legalOpen, setLegalOpen] = useState<LegalKind | null>(null);
  // Controlled menu state: the compact legal row uses plain buttons (not
  // DropdownMenuItems), so we must close the menu ourselves before opening
  // the overlay — otherwise the open Radix menu traps pointer events.
  const [menuOpen, setMenuOpen] = useState(false);
  const openLegal = (kind: LegalKind) => {
    setMenuOpen(false);
    setLegalOpen(kind);
  };

  // Register presence so the fixed legal footer knows the legal links are
  // reachable via this menu on the current page (only while we actually
  // render — not when returning null below).
  useRegisterUserMenu(!isLoading && !!isAuthenticated && !!user);

  const { data: statuses } = useQuery<StatusData[]>({
    queryKey: ["/api/users/availability-statuses"],
    enabled: !!user,
  });

  const { data: badge } = useQuery<{ count: number }>({
    queryKey: ["/api/time-off/badge"],
    enabled: !!user,
    refetchInterval: 60_000,
  });
  const badgeCount = badge?.count ?? 0;

  const myStatus = statuses?.find((s) => s.id === user?.id);
  const currentStatus = myStatus?.availabilityStatus || "working";
  const savedTimers = myStatus?.statusTimers || {};

  const statusMutation = useMutation({
    mutationFn: ({ status, timerMinutes }: { status: UserStatusKey; timerMinutes?: number }) =>
      apiRequest("PATCH", "/api/user/availability-status", { status, timerMinutes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users/availability-statuses"] });
    },
  });

  const handleLogout = async () => {
    // Server must clear the httpOnly auth cookies — client JS cannot.
    try {
      await authedFetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } catch { /* proceed with local cleanup regardless */ }
    const { clearAuth } = await import("@/lib/auth");
    clearAuth();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <TooltipProvider delayDuration={250}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="navy-card border-gray-600 text-gray-300 hover:bg-gray-700 relative"
                  aria-label="User Menu"
                >
                  <User className="w-4 h-4" />
                  {user.id && <StatusBubble userId={user.id} size="sm" showTooltip={false} className="absolute -bottom-1 -right-1" />}
                  {badgeCount > 0 && (
                    <span className="absolute -top-1 -left-1 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 font-medium text-center">
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-gray-800 text-gray-200 text-xs border-gray-600">
              User Menu
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent align="end" className="w-96 bg-gray-800 border-gray-600" onCloseAutoFocus={event => { if (settingsOpen) event.preventDefault(); }}>
          <DropdownMenuLabel className="text-gray-300">
            <div className="flex items-center justify-between">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}`
                    : user.username}
                </p>
              </div>
              <IconTooltip label="Account settings">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-200 hover:bg-gray-700 h-7 w-7 p-0"
                  onClick={() => { setMenuOpen(false); setSettingsOpen(true); }}
                  title="Account settings"
                  aria-label="Account settings"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </IconTooltip>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-gray-700" />
          <DropdownMenuLabel className="text-xs text-gray-500 py-1">Status</DropdownMenuLabel>
          <StatusPicker
            currentStatus={currentStatus}
            savedTimers={savedTimers}
            statusExpiresAt={myStatus?.statusExpiresAt || null}
            onSelect={(status, timerMinutes) => statusMutation.mutate({ status, timerMinutes })}
          />
          <DropdownMenuSeparator className="bg-gray-700" />
          <DropdownMenuItem
            onClick={() => setTimeOffOpen(true)}
            className="text-gray-300 hover:bg-gray-700 hover:text-white cursor-pointer"
          >
            <CalendarOff className="mr-2 h-4 w-4" />
            <span className="flex-1">Time Off</span>
            {badgeCount > 0 && (
              <span className="ml-auto min-w-[18px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 font-medium text-center">
                {badgeCount > 99 ? "99+" : badgeCount}
              </span>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-gray-700" />
          <DropdownMenuItem
            onClick={handleLogout}
            className="text-gray-300 hover:bg-gray-700 hover:text-white cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-gray-700" />
          {/* Task #141: both legal links share ONE compact row (like the
              version line below) instead of two full menu items. */}
          <div className="flex items-center justify-center gap-2 px-2 py-1.5 text-xs text-gray-400">
            <Scale className="h-3.5 w-3.5 text-gray-500" />
            <button
              type="button"
              onClick={() => openLegal('impressum')}
              className="hover:text-white hover:underline cursor-pointer"
              data-testid="menu-impressum"
            >
              Impressum
            </button>
            <span className="text-gray-600" aria-hidden="true">·</span>
            <button
              type="button"
              onClick={() => openLegal('datenschutz')}
              className="hover:text-white hover:underline cursor-pointer"
              data-testid="menu-datenschutz"
            >
              Datenschutz
            </button>
          </div>
          <DropdownMenuSeparator className="bg-gray-700" />
          <div className="px-2 py-1.5 text-center">
            <span className="text-xs text-gray-500">V1.44.0</span>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <AccountSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <TimeOffDialog open={timeOffOpen} onOpenChange={setTimeOffOpen} />
      {legalOpen && <LegalModal kind={legalOpen} surface="app" onClose={() => setLegalOpen(null)} />}
    </>
  );
}
